import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Phone, Mail, Bell, ChevronDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import Logo from './Logo';
import NewsletterSignup from './NewsletterSignup';

const languages = [
  { code: 'fr', label: 'FR', name: 'Français' },
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'nl', label: 'NL', name: 'Nederlands' },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const changeLanguage = (lng) => i18n.changeLanguage(lng);

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/inventory', label: t('nav.inventory') },
    { path: '/sell-your-car', label: t('nav.sell') },
    { path: '/booking', label: t('nav.booking') },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm'
          : 'bg-white border-b border-slate-100'
      }`}
      data-testid="site-header"
    >
      {/* Top utility bar */}
      <div
        className={`bg-[#0F172A] text-white text-xs transition-all duration-300 overflow-hidden ${
          isScrolled ? 'max-h-0 opacity-0' : 'max-h-10 opacity-100'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-10 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <a
              href="tel:+32489432161"
              className="flex items-center gap-1.5 hover:text-emerald-300 transition-colors"
              data-testid="header-phone-link"
            >
              <Phone size={12} />
              <span className="hidden sm:inline">+32 489 43 21 61</span>
            </a>
            <a
              href="mailto:info@autodistrict.be"
              className="hidden md:flex items-center gap-1.5 hover:text-emerald-300 transition-colors"
              data-testid="header-email-link"
            >
              <Mail size={12} />
              info@autodistrict.be
            </a>
          </div>
          <div className="flex items-center gap-5">
            <Dialog>
              <DialogTrigger asChild>
                <button
                  className="hidden sm:flex items-center gap-1.5 text-white/90 hover:text-emerald-300 transition-colors"
                  data-testid="header-newsletter-btn"
                >
                  <Bell size={12} />
                  {t('newsletter.headerCta', "S'abonner à la newsletter")}
                </button>
              </DialogTrigger>
              <DialogContent className="bg-white border border-slate-200 max-w-md text-slate-900">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('newsletter.modalTitle', 'Restez informé')}
                  </DialogTitle>
                </DialogHeader>
                <NewsletterSignup variant="light" />
              </DialogContent>
            </Dialog>
            <span className="hidden sm:inline text-emerald-300 font-medium tracking-wide">
              {t('contact.appointmentOnly', 'Sur rendez-vous')}
            </span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" data-testid="header-logo-link" className="flex-shrink-0">
            <Logo size="md" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}

            <Select value={i18n.language} onValueChange={changeLanguage}>
              <SelectTrigger
                className="w-[70px] h-9 bg-white border-slate-200 text-slate-700 text-xs font-semibold tracking-wider uppercase rounded-full px-3"
                data-testid="language-switcher"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 rounded-xl">
                {languages.map((lang) => (
                  <SelectItem
                    key={lang.code}
                    value={lang.code}
                    className="text-slate-700 hover:text-slate-900 text-sm"
                  >
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Link to="/booking" className="btn-primary text-xs px-5 py-2.5" data-testid="header-book-cta">
              {t('nav.booking')}
            </Link>
          </div>

          {/* Mobile menu trigger */}
          <button
            className="lg:hidden p-2 text-slate-700 hover:text-slate-900 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="lg:hidden pb-6 border-t border-slate-100 pt-4 fade-up" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {navItems.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-blue-50 text-blue-900'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    className="mt-2 flex items-center justify-center gap-2 text-sm font-semibold text-blue-900 border border-blue-200 bg-blue-50 px-4 py-3 rounded-full hover:bg-blue-100 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                    data-testid="header-newsletter-mobile-btn"
                  >
                    <Bell size={14} />
                    {t('newsletter.headerCta', "S'abonner à la newsletter")}
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-white border border-slate-200 max-w-md text-slate-900">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {t('newsletter.modalTitle', 'Restez informé')}
                    </DialogTitle>
                  </DialogHeader>
                  <NewsletterSignup variant="light" />
                </DialogContent>
              </Dialog>
              <div className="pt-4 mt-2 border-t border-slate-100">
                <Select value={i18n.language} onValueChange={changeLanguage}>
                  <SelectTrigger className="w-full h-10 bg-white border-slate-200 text-slate-700 text-xs font-semibold tracking-wider uppercase rounded-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-xl">
                    {languages.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code} className="text-slate-700">
                        {lang.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
